import { PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wrench } from "lucide-react";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import { useAdmin } from "@/hooks/useAdmin";
import { useAuth } from "@/components/auth/AuthProvider";

/**
 * MaintenanceGate
 *
 * Global gate that renders a full-screen "we're back soon" message when
 * app_settings.maintenance_mode is true. Admins bypass so they can keep
 * working (including disabling maintenance mode from /admin/settings),
 * and the /admin/* routes themselves stay reachable for the same
 * reason — otherwise an accidental toggle would lock everyone out.
 *
 * Also lets public auth/legal/share routes through so users mid-flow
 * (Stripe return, password reset, /w/:slug share links) don't see the
 * gate either.
 */
export function MaintenanceGate({ children }: PropsWithChildren) {
  const { settings, isLoading } = usePublicSettings();
  const { isAdmin } = useAdmin();
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Don't gate while settings are still loading on first paint — better
  // to briefly show the app than to briefly show "we're back soon" and
  // then un-show it. The query has a 30-min stale time so after the
  // first hit this is basically synchronous.
  if (isLoading) return <>{children}</>;

  if (!settings.maintenance_mode) return <>{children}</>;

  // Admins always bypass. Without this, an admin who toggles
  // maintenance ON would lose access to /admin/settings to toggle it
  // OFF — the only way back would be a direct DB edit.
  if (isAdmin) return <>{children}</>;

  // Let admin routes themselves render so an admin who isn't yet
  // detected as such by useAdmin (e.g. cold cache) can still navigate
  // to /admin/login and authenticate.
  if (location.pathname.startsWith("/admin")) return <>{children}</>;

  // Public routes that must work in maintenance: login (so an admin
  // can sign in), legal pages, public share links, and Stripe return.
  const bypassPaths = ["/login", "/signup", "/terms", "/privacy"];
  if (bypassPaths.includes(location.pathname)) return <>{children}</>;
  if (location.pathname.startsWith("/w/")) return <>{children}</>;
  if (location.pathname.startsWith("/subscription/")) return <>{children}</>;

  // If we made it here and the user is anonymous on the landing page,
  // still let the landing render — no point gating marketing.
  if (!user && location.pathname === "/") return <>{children}</>;

  const message =
    settings.maintenance_message?.trim() ||
    t("maintenance.default_message");

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-16 h-16 rounded-full bg-brand/10 text-brand grid place-items-center">
          <Wrench className="w-8 h-8" aria-hidden />
        </div>
        <div className="space-y-2">
          <h1 className="font-display text-3xl text-text">
            {t("maintenance.title")}
          </h1>
          <p className="text-muted leading-relaxed whitespace-pre-line">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceGate;
